<template>
    <div v-if="loadingStatus" class="text-xs-center" style="padding: 50px">
        <v-progress-circular indeterminate color="typography3" />
    </div>
    <div v-else>
        <v-card-title>
            <h3 class="headline mb-0">{{ recordType }} of&nbsp;</h3>
            <v-tooltip location="top" :max-width="700">
                <template v-slot:activator="{ props }">
                    <v-btn
                        size="small"
                        color="secondary"
                        class="btn"
                        variant="text"
                        v-bind="props"
                    >
                        <h3 class="headline mb-0">{{ calcName }}</h3>
                    </v-btn>
                </template>
                <v-row
                    v-for="(item, index) in calcDisplayList"
                    :key="index"
                    class="calc-list"
                >
                    <v-col cols="2">{{ item.name }}</v-col>
                    <v-col cols="10">{{ item.value }}</v-col>
                </v-row>
            </v-tooltip>

            <h3 class="headline mb-0">revision {{ storedRevision }} by</h3>
            <v-tooltip location="top" :max-width="250">
                <template v-slot:activator="{ props }">
                    <v-btn
                        class="btn"
                        variant="text"
                        size="small"
                        color="secondary"
                        v-bind="props"
                    >
                        <h3 class="headline mb-0">{{ displayName }}</h3>
                    </v-btn>
                </template>
                <v-row
                    v-for="(item, index) in userDisplayList"
                    :key="index"
                    class="calc-list"
                >
                    <v-col cols="4">{{ item.name }}</v-col>
                    <v-col cols="8">{{ item.value }}</v-col>
                </v-row>
            </v-tooltip>
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="auto" class="pa-1">
                    <v-avatar size="150">
                        <img v-if="photo" :src="photo" />
                        <v-icon v-else>mdi-account-circle</v-icon>
                    </v-avatar>
                </v-col>
                <v-col cols="auto" class="pa-1" style="margin-left: 20px">
                    <div class="pa-1">
                        <span
                            class="
                                subheading
                                blue
                                white--text
                                pa-1
                                text-uppercase
                            "
                            >{{ displayStatus }}</span
                        >
                    </div>
                    <div v-show="referenceString !== ''" class="pa-1">
                        <p class="subheading pa-0 ma-0">
                            {{ approvalType }} {{ getReferenceString }}
                        </p>
                    </div>
                    <div class="pa-1">
                        <p
                            class="
                                subheading
                                font-weight-light font-italic
                                pt-2
                            "
                        >
                            "{{ baseComment.content }}"
                        </p>
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

const props = defineProps({
    recordType: { type: String },
    referenceString: { default: '', type: String },
})

const store = useStore()
const route = useRoute()

const selectedRecord = computed(() => store.state.selectedRecord)

const user = ref({})
const photo = ref(null)
const loadingStatus = ref(true)
const baseComment = ref({})
const calc = ref({})
const storedRevision = ref(null)
const calcDisplayList = ref({})
const userDisplayList = ref({})

onMounted(async () => {
    user.value = await store.dispatch(
        'getUser',
        selectedRecord.value.created_by.email
    )
    photo.value = await store.dispatch(
        'getUserPhoto',
        selectedRecord.value.created_by.email
    )
    setUserDisplayList(user.value)
    baseComment.value = await store.dispatch(
        'getComment',
        selectedRecord.value.comment_id
    )
    prepareComment(baseComment.value)
    await setCalc()
})

const calcName = computed(() =>
    calc.value && calc.value.name ? calc.value.name : ''
)

const displayStatus = computed(() => {
    let status = selectedRecord.value.type.name
    if (status === 'not_recommended') return 'not recommended'
    if (status === 'recommended_caveats') return 'recommended with conditions'
    return status
})

const displayName = computed(() => {
    if (user.value.displayName) return user.value.displayName
    return user.value.mail
})

const getReferenceString = computed(() => {
    if (props.referenceString.includes('/')) {
        return props.referenceString.split('/')[0]
    }
    return props.referenceString
})

const approvalType = computed(() => {
    if (props.referenceString.includes('/')) return 'Project:'
    return 'Skills Network:'
})

function setListDetails(c) {
    calcDisplayList.value = [
        { name: 'Name:', value: c.name },
        { name: 'Description:', value: c.description },
        { name: 'Author:', value: c.authorEmail },
        { name: 'Level of Review:', value: c.levelOfReview },
        { name: 'Revisions:', value: displayRevisions(c.revisions) },
        { name: 'Full Name:', value: c.fullname },
    ]
    loadingStatus.value = false
}

function displayRevisions(revisions) {
    let string = revisions[0]
    if (revisions.length > 1) {
        for (let i = 1; i < revisions.length; i++) string += ', ' + revisions[i]
    }
    return string
}

function setUserDisplayList(u) {
    let list = []
    if (u.displayName) list.push({ name: 'Name:', value: u.displayName })
    list.push({ name: 'Email:', value: u.mail })
    if (u.jobTitle) list.push({ name: 'Title:', value: u.jobTitle })
    if (u.officeLocation)
        list.push({ name: 'Office:', value: u.officeLocation })
    userDisplayList.value = list
}

function prepareComment(comment) {
    if (comment.content === ' ') comment.content = ''
    baseComment.value = comment
}

async function setCalc() {
    const path = route.path
    const path_parts = path.split('/')
    const calc_id = parseInt(path_parts[2])
    const c = await store.dispatch('getCalc', { id: calc_id })
    calc.value = c
    storedRevision.value = c.revision
    setListDetails(c)
}
</script>

<style>
.btn {
    text-transform: none !important;
    padding: 0px 8px 0px 8px !important;
    margin: 0px !important;
}
.calc-list {
    padding: 5px 20px 5px 5px;
}
</style>
